import { useState, type ReactNode } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { Button, Spinner } from "./ui";

/**
 * Tabla de datos con TanStack Table (CLAUDE.md Fase 4).
 *
 * Se configura con `getCoreRowModel` + `getSortedRowModel`: el listado de
 * DeCA y los catálogos son operativos, se leen y se ordenan, no se paginan
 * en cliente — la paginación la resuelve la API (offset/limit), que es donde
 * está el dato completo.
 */

export interface DataTableProps<T> {
  /**
   * `ColumnDef<T, any>` y no `<T, unknown>`: `createColumnHelper` fija el
   * tipo del **valor de la celda** en cada columna (p. ej.
   * `ColumnDef<Vehicle, number | null>`), y `unknown` no admite esa
   * varianza. El tipo del valor no se usa aquí: la tabla solo pinta lo que
   * devuelve el `cell` de cada columna.
   */
  columns: Array<ColumnDef<T, any>>;
  data: T[];
  isLoading?: boolean;
  emptyMessage?: ReactNode;
  /** Texto de la fila de carga; se muestra solo la primera vez. */
  loadingLabel?: string;
}

export function DataTable<T>({
  columns,
  data,
  isLoading = false,
  emptyMessage,
  loadingLabel = "Cargando datos",
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading && data.length === 0) return <Spinner label={loadingLabel} />;

  if (data.length === 0) {
    return (
      <div className="surface px-4 py-10 text-center text-sm text-ink-500 dark:text-ink-400">
        {emptyMessage ?? "No hay resultados con estos filtros."}
      </div>
    );
  }

  return (
    <div className="surface overflow-x-auto">
      <table className="w-full min-w-[46rem] border-collapse">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                return (
                  <th key={header.id} className="th whitespace-nowrap">
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 uppercase tracking-[0.12em] hover:text-ink-800 dark:hover:text-ink-100"
                        aria-label={`Ordenar por ${header.column.id}`}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span aria-hidden="true">
                          {header.column.getIsSorted() === "asc"
                            ? "▲"
                            : header.column.getIsSorted() === "desc"
                              ? "▼"
                              : "↕"}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="td align-top">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Paginación por offset, tal y como la expone la API (`page`, `pageSize`). */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <nav
      className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-ink-600 dark:text-ink-300"
      aria-label="Paginación"
    >
      <p className="num">
        {from}–{to} de {total}
      </p>
      <div className="flex items-center gap-2">
        <Button onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          Anterior
        </Button>
        <span className="num px-1" aria-current="page">
          {page} / {lastPage}
        </span>
        <Button onClick={() => onPageChange(page + 1)} disabled={page >= lastPage}>
          Siguiente
        </Button>
      </div>
    </nav>
  );
}

export { createColumnHelper };
export type { ColumnDef };
